import { useLocation } from 'react-router-dom';

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface SiteSchemaProps {
  pageType?: 'WebPage' | 'Article' | 'AboutPage';
  breadcrumbs?: BreadcrumbItem[];
  articleData?: {
    headline: string;
    description: string;
    author: string;
    datePublished: string;
    dateModified: string;
    image: string;
  };
  faqData?: { question: string; answer: string }[];
}

export default function SchemaOrg({ pageType = 'WebPage', breadcrumbs, articleData, faqData }: SiteSchemaProps) {
  const location = useLocation();
  const baseUrl = 'https://www.hanmancomic.com';
  const currentUrl = `${baseUrl}${location.pathname}`;

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩漫漫画网',
    url: baseUrl,
    description: '韩漫漫画网汇集韩国漫画大全，推荐最新热门韩国漫画，提供无删减韩国漫画排行榜。',
    sameAs: [baseUrl],
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '韩漫漫画网',
    url: baseUrl,
    description: '韩漫漫画网汇集韩国漫画大全，推荐最新热门韩国漫画，提供无删减韩国漫画排行榜。',
    inLanguage: 'zh-CN',
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': pageType,
    url: currentUrl,
    name: document.title,
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫漫画网',
      url: baseUrl,
    },
    inLanguage: 'zh-CN',
  };

  let breadcrumbSchema = null;
  if (breadcrumbs && breadcrumbs.length > 0) {
    breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: `${baseUrl}${item.url}`,
      })),
    };
  }

  let articleSchema = null;
  if (articleData) {
    articleSchema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: articleData.headline,
      description: articleData.description,
      author: {
        '@type': 'Person',
        name: articleData.author,
      },
      datePublished: articleData.datePublished,
      dateModified: articleData.dateModified,
      image: articleData.image,
      publisher: {
        '@type': 'Organization',
        name: '韩漫漫画网',
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': currentUrl,
      },
    };
  }

  let faqSchema = null;
  if (faqData && faqData.length > 0) {
    faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqData.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    };
  }

  const schemas = [organizationSchema, websiteSchema, webpageSchema];
  if (breadcrumbSchema) schemas.push(breadcrumbSchema);
  if (articleSchema) schemas.push(articleSchema);
  if (faqSchema) schemas.push(faqSchema);

  return (
    <>
      {schemas.map((schema, index) => (
        <script
          key={`schema-${index}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}
    </>
  );
}